"use client";

import { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ArrowRight, ChevronLeft } from "lucide-react";
import Link from "next/link";

const MIN_DELIVERIES = 20;
const MAX_DELIVERIES = 500;
const DEFAULT_DELIVERIES = 120;
const AVG_TICKET = 45; // USD — the order value used across this page
const DOORDASH_COMMISSION_RATE = 0.25; // EXAMPLE marketplace commission (25%) — labeled as an example in the copy; real rates vary by app and plan
const REDY_FEE_PER_DELIVERY = 2.99; // per DELIVERED delivery

const DOORDASH_PER_ORDER = AVG_TICKET * DOORDASH_COMMISSION_RATE; // $11.25

function useOdometer(target: number, durationMs = 700) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const start = display;
    const delta = target - start;
    const startedAt = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(start + delta * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return display;
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

export default function DeliveryHero() {
  const { t } = useLang();
  const [deliveries, setDeliveries] = useState(DEFAULT_DELIVERIES);

  const doordashTake = deliveries * DOORDASH_PER_ORDER;
  const redyCost = deliveries * REDY_FEE_PER_DELIVERY;
  const difference = doordashTake - redyCost;

  const doordashDisplay = useOdometer(doordashTake);
  const redyDisplay = useOdometer(redyCost);
  const differenceDisplay = useOdometer(difference);
  const settleControls = useAnimation();

  const handleRelease = () => {
    settleControls.start({
      scale: [1, 1.04, 1],
      transition: { duration: 0.45, ease: [0.21, 0.47, 0.32, 0.98] },
    });
  };

  return (
    <section className="relative pt-32 pb-24 px-6 overflow-hidden bg-[#FAFAF8]">
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t(
            "Redy Delivery · Powered by Uber Direct",
            "Redy Delivery · Con la red de Uber Direct"
          )}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[40px] sm:text-6xl md:text-[80px] lg:text-[96px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("Stop paying commission", "Deja de pagar comisión")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("on your regulars.", "por tus clientes de siempre.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[680px] mx-auto leading-relaxed mb-16"
        >
          {t(
            "Marketplace apps are good at bringing you new customers. What's expensive is paying commission on every order from people who already know you and would have ordered anyway.",
            "Las apps de marketplace son buenas para traerte clientes nuevos. Lo caro es pagar comisión en cada pedido de gente que ya te conoce y que iba a ordenar de todas formas."
          )}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative max-w-[920px] mx-auto"
        >
          <div
            className="absolute -inset-4 rounded-[40px] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, rgba(244,114,43,0.12) 0%, transparent 65%)" }}
          />

          <motion.div
            animate={settleControls}
            className="relative bg-white rounded-[32px] border border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.06)] p-8 md:p-12"
          >
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-2">
              {t("Delivery orders you already get, per month", "Pedidos a domicilio que ya recibes, al mes")}
            </div>

            <div className="flex items-end justify-between mb-3">
              <div className="text-[42px] md:text-[56px] font-black tracking-tight text-redy-charcoal leading-none tabular-nums">
                {deliveries.toLocaleString()}
              </div>
              <div className="text-[12px] font-bold text-redy-secondary mb-2">
                {t("deliveries", "envíos")} · ${AVG_TICKET} {t("avg order", "pedido prom")}
              </div>
            </div>

            <input
              type="range"
              min={MIN_DELIVERIES}
              max={MAX_DELIVERIES}
              step={5}
              value={deliveries}
              onChange={(e) => setDeliveries(Number(e.target.value))}
              onMouseUp={handleRelease}
              onTouchEnd={handleRelease}
              aria-label={t("Deliveries per month", "Envíos por mes")}
              className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-[#F4722B] to-[#D7263D]/30 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[#F4722B] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab active:[&::-webkit-slider-thumb]:cursor-grabbing"
            />

            <div className="flex justify-between text-[11px] font-bold text-redy-secondary/50 mt-2">
              <span>{MIN_DELIVERIES}</span>
              <span>{MAX_DELIVERIES}+</span>
            </div>

            <div className="mt-4 text-[12px] font-medium text-redy-secondary/70">
              {t("That's", "Eso es")}{" "}
              <span className="font-bold text-redy-charcoal tabular-nums">
                {formatCurrency(deliveries * AVG_TICKET)}
              </span>{" "}
              {t("in delivery sales per month.", "en ventas a domicilio al mes.")}
            </div>

            <div className="h-px bg-black/5 my-10" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 mb-8">
              <div className="text-left">
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
                  {t("Marketplace commission (example)", "Comisión de marketplace (ejemplo)")}
                </div>
                <div className="text-[40px] md:text-[52px] font-black tracking-tight text-redy-charcoal/40 leading-none tabular-nums">
                  −{formatCurrency(doordashDisplay)}
                </div>
                <div className="text-[12px] font-medium text-redy-secondary/70 mt-2">
                  {t(
                    "Example at 25% of the order value — rates vary by app and plan. ~$11.25 on a $45 order.",
                    "Ejemplo con 25% del valor del pedido — la tasa varía según la app y el plan. ~$11.25 en un pedido de $45."
                  )}
                </div>
              </div>

              <div className="text-left relative">
                <div
                  className="absolute -inset-x-4 -inset-y-3 rounded-[20px] pointer-events-none"
                  style={{ background: "radial-gradient(ellipse, rgba(244,114,43,0.08) 0%, transparent 70%)" }}
                />
                <div className="relative">
                  <div className="text-[10px] font-black tracking-widest uppercase mb-3" style={{ color: "#D7263D" }}>
                    {t("Redy costs you", "Redy te cuesta")}
                  </div>
                  <div className="text-[40px] md:text-[52px] font-black tracking-tight leading-none tabular-nums" style={{ color: "#D7263D" }}>
                    −{formatCurrency(redyDisplay)}
                  </div>
                  <div className="text-[12px] font-medium mt-2" style={{ color: "#D7263D", opacity: 0.7 }}>
                    {t(
                      "From $2.99 per delivered delivery, by plan. 0% commission.",
                      "Desde $2.99 por envío entregado, según tu plan. 0% de comisión."
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#F4722B] to-[#D7263D] rounded-[24px] px-6 py-6 md:px-8 md:py-8 text-white shadow-lg shadow-[#F4722B]/20">
              <div className="flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
                <div>
                  <div className="text-[11px] font-black tracking-widest uppercase text-white/70 mb-2">
                    {t("The difference, back in your pocket", "La diferencia, de vuelta a tu bolsillo")}
                  </div>
                  <div className="text-[48px] md:text-[64px] font-black leading-none tabular-nums">
                    +{formatCurrency(differenceDisplay)}
                  </div>
                  <div className="text-[13px] font-bold text-white/85 mt-1">
                    /{t("month", "mes")} · {formatCurrency(differenceDisplay * 12)}/{t("year", "año")}
                  </div>
                </div>
                <Link
                  href="/signup"
                  className="inline-flex items-center gap-2 bg-white text-redy-charcoal font-bold text-[14px] px-6 py-3.5 rounded-full hover:scale-[1.03] transition-transform shadow-lg whitespace-nowrap"
                >
                  {t("Request Delivery", "Solicitar Delivery")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-8 text-[12px] font-medium text-redy-secondary/60 max-w-[620px] mx-auto"
        >
          {t(
            "Keep DoorDash for discovery if it's working — that's what it's genuinely good at. This is for the regulars who already know your name.",
            "Quédate con DoorDash para descubrimiento si te funciona — en eso sí es bueno. Esto es para los de siempre, los que ya saben tu nombre."
          )}
        </motion.div>
      </div>
    </section>
  );
}
